import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged, finalize, Subject, takeUntil } from 'rxjs';
import { Patient } from '../../../../models/patient';
import { AuxiliarService } from '../../../../services/auxiliar.service';
import { PatientService } from '../../../../services/patient.service';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import _ from 'lodash';
import { environment } from '../../../../../environments/environment';
import moment from 'moment';
import { duplicatedValidator } from '../../../../utils/validators/duplicateValidator';

@Component({
  selector: 'app-patient-info-drawer',
  standalone: false,
  templateUrl: './patient-info-drawer.component.html',
  styleUrl: './patient-info-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PatientInfoDrawerComponent implements OnInit, OnDestroy {
  @Input() patient: Patient;

  public binaryOptions: {name: string, value: boolean}[] = [];
  public cardholderEndMsg: string;
  public cardholderEndSeverity: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private editDiscountPermission: boolean = true;
  public form: FormGroup;
  public loading: boolean;
  public loadingAvatar: boolean = true;
  public loadingTaxcode: boolean;
  public patientAvatar: string;
  public patientAvatarBlob: Blob;

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private patientService: PatientService,
    private translate: TranslateService,
    private logger: NGXLogger,
  ){}

  ngOnInit(): void {
    // rgpd / cardholder options
    this.binaryOptions = this.auxiliarService.getBinaryOptions();
    // load form
    this.loadForm();
    // get patient avatar
    this.getPatientAvatar();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      address: [this.patient?.address || '', []],
      birthdate: [this.patient?.birthdate, []],
      cardholder: [!!(this.patient?.cardholder), []],
      cardholderEnd: [{value: this.patient?.cardholderEnd ? moment(this.patient?.cardholderEnd).toDate() : 
        (this.patient?.cardholder ? moment().add(1, 'year').toDate() : null), disabled: !this.patient?.cardholder}, []],
      city: [this.patient?.city || '', []],
      discount: [{
        value: this.patient?.discount || (this.patient?.cardholder ? environment.cardholderDiscount : 0), 
        disabled: this.patient?.cardholder}, [Validators.min(0), Validators.max(100)]],
      email: [this.patient?.email || '', [Validators.email]],
      firstname: [this.patient?.firstname || '', [Validators.required]],
      id: [this.patient?.id || '', []],
      lastname: [this.patient?.lastname || '', [Validators.required]],
      phone: [this.formatPhone(this.patient?.phone), [Validators.pattern(environment.pattern.phone)]],
      rgpd: [this.patient?.rgpd || false, [Validators.required]],
      state: [this.patient?.state || '', []],
      taxcode: [this.patient?.taxcode || '', []],
      tutorName: [this.patient?.tutorName || '', []],
      tutorTaxcode: [this.patient?.tutorTaxcode || '', []],
      zipcode: [this.patient?.zipcode || '', []],
    });

    // edit mode
    if(this.patient) {
      this.f['id'].setValidators([Validators.required]);

      if(this.patient?.cardholder) {
        this.f['cardholderEnd'].setValidators([Validators.required]);
      }
      if(this.patient?.cardholderEnd) {
        const diffDays: number = moment(this.patient?.cardholderEnd).diff(moment(), 'days');
        if(moment(this.patient?.cardholderEnd).isBefore(moment())) {
          this.editDiscountPermission = false;
          this.cardholderEndSeverity = 'error';
          this.cardholderEndMsg = this.translate.instant('error.cardholder-expired');
        } else if (diffDays < 30) {
          this.cardholderEndSeverity = 'warn';
          this.cardholderEndMsg = this.translate.instant('error.cardholder-days', 
            {days: `${diffDays} ${this.translate.instant(diffDays === 1 ? 'day' : 'days')}`});
        }
      }
    }

    // sessionBegin value change
    this.f['cardholder'].valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: boolean) => {
        if(value) {
          this.f['cardholderEnd'].enable();
          this.f['cardholderEnd'].setValue(moment().add(1, 'year').toDate());
          this.f['cardholderEnd'].setValidators([Validators.required]);
          this.f['discount'].setValue(environment.cardholderDiscount);
          this.f['discount'].disable();
        } else {
          this.f['cardholderEnd'].disable();
          this.f['cardholderEnd'].setValue('');
          this.f['cardholderEnd'].setValidators([]);
          if(this.editDiscountPermission) this.f['discount'].enable();
        }
        this.f['cardholderEnd'].updateValueAndValidity();
      });
  }

  private formatPhone(phone: string): string {
    if(!phone) return '';
    return _.replace(phone, '+34', '');
  }

  public checkTaxcode(): void {
    if(!this.f['taxcode'].value || this.f['taxcode'].value === this.patient?.taxcode) {
      this.f['taxcode'].setValidators([]);
      this.f['taxcode'].updateValueAndValidity();
      return;
    };
    this.loadingTaxcode = true;
    this.patientService.getPatientByTaxcode(this.f['taxcode'].value).pipe(
      finalize(() => this.loadingTaxcode = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.logger.debug('[patientService] getPatientByTaxcode res: ', res);
        // set validators
        if(_.get(res, 'body.data.taxcode')) {
          this.f['taxcode'].setValidators([duplicatedValidator(_.castArray(_.get(res, 'body.data.taxcode')),
            this.translate.instant('error.patient-taxcode-duplicated', {
              taxcode: this.f['taxcode'].value, 
              patient: _.get(res, 'body.data.givenname')
            })
          )]);
        } else {
          this.f['taxcode'].setValidators([]);
        }
        this.f['taxcode'].updateValueAndValidity();
      },
      error: (err: any) => {
        this.logger.error('[patientService] getPatientByTaxcode err:', err);
      }
    });
  }

  private getPatientAvatar(): void {
    if(!this.patient) {
      this.patientAvatar = this.auxiliarService.generateInitialsAvatar('', 200);
      this.loadingAvatar = false;
    } else {
      this.patientService.getPatientAvatar(this.patient.id).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: async (res: any) => {
          this.logger.debug('[patientService] getPatientAvatar res: ', res);
          const avatarBlob = _.get(res, 'body');
          if(avatarBlob?.size > 0) {
            const avatar = await this.auxiliarService.convertBlobToBase64(avatarBlob);
            this.patientAvatar = avatar || this.auxiliarService.generateInitialsAvatar(this.patient?.givenname, 200);
          } else {
            this.patientAvatar = this.auxiliarService.generateInitialsAvatar(this.patient?.givenname, 200);
          }
          this.loadingAvatar = false;
        },
        error: (err: any) => {
          this.logger.error('[patientService] getPatientAvatar err:', err);
          this.patientAvatar = this.auxiliarService.generateInitialsAvatar(this.patient?.givenname, 200);
          this.loadingAvatar = false;
        }
      });
    }
  }

  public setPatientAvatar($event: { photo: string, blob: Blob }): void {
    this.patientAvatar = $event.photo;
    this.patientAvatarBlob = $event.blob;
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.loading = true;
      const formData = this.form.getRawValue();
      if(formData.phone) {
        _.set(formData, 'phone', `+34${formData.phone}`);
      }
      if(formData.cardholderEnd) {
        _.set(formData, 'cardholderEnd', moment(formData.cardholderEnd).format('YYYY-MM-DD'));
      }
      if(this.patient) {
        // update patient
        this.patientService.updatePatient(formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[patientService] updatePatient res: ', res);
            if(this.patientAvatarBlob?.size) {
              this.updatePatientAvatar(_.get(res, 'body.data'));
            } else {
              this.auxiliarService.closeDrawer('left', _.get(res, 'body.data'));
            }
            this.auxiliarService.toast(this.translate.instant('success.patient-update'), 'success');
          },
          error: (err: any) => {
            this.logger.error('[patientService] updatePatient err:', err);
            this.auxiliarService.toast(this.translate.instant('error.patient-update'), 'error');
          }
        });
      } else {
        // create patient
        this.patientService.createPatient(formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[patientService] createPatient res: ', res);
            if(this.patientAvatarBlob?.size) {
              this.updatePatientAvatar(_.get(res, 'body.data'));
            } else {
              this.auxiliarService.closeDrawer('left', _.get(res, 'body.data'))
            }
            this.auxiliarService.toast(this.translate.instant('success.patient-create'), 'success');
          },
          error: (err: any) => {
            this.logger.error('[patientService] createPatient err:', err);
            this.auxiliarService.toast(this.translate.instant('error.patient-create'), 'error');
          }
        });
      }
    }
  }

  public updatePatientAvatar(patient: Patient): void {
    this.patientService.uploadPatientAvatar(patient, this.patientAvatarBlob).pipe(
      finalize(() => this.auxiliarService.closeDrawer('left', patient)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.logger.debug('[patientService] uploadPatientAvatar res: ', res);
      },
      error: (err: any) => {
        this.logger.error('[patientService] uploadPatientAvatar err:', err);
        this.auxiliarService.toast(this.translate.instant('error.patient-avatar'), 'error');
      }
    });
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer('left');
  }
}
